import { Alert, Button, Snackbar } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { fetchAdminContacts } from "@/api/adminContacts";
import {
  buildColumns,
  SORTABLE_COLUMN_IDS,
  type SortableContactColumnId,
} from "@/components/admin/contacts/adminContactsColumns";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import ColumnConfigPopover, { type ColumnConfigItem } from "@/components/ColumnConfigPopover";
import {
  ADMIN_CONTACTS_TEXT,
  COLS_PARAM,
  COLUMN_LABELS,
  DEFAULT_HIDDEN,
  DEFAULT_ORDER,
  MIN_WIDTH,
  PAGE_SIZE,
} from "@/constants/adminContacts";
import {
  CAMPAIGN_LINK_STATUS_VALUES,
  type CampaignLinkStatus,
  statusVisual,
} from "@/constants/campaings";
import { useAdminContacts } from "@/hooks/useAdminContacts";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";
import type {
  AdminContactListParams,
  AdminContactRow,
  AdminContactsFilterOptions,
  ContactColumnId,
  Option,
} from "@/interfaces/adminContacts";

/** The slice of the Blotter's query state that maps to API params. Exports omit the sort. */
interface ContactsQuery {
  search: string;
  filters: AppliedFilter[];
  page: number;
  limit: number;
}

/**
 * Copy for the two failure paths. Local to this file rather than in `constants/adminContacts.ts`'s
 * text block because nothing else reads it; the strings other units share still live there.
 */
const FAILURE_COPY = {
  list: "We couldn't load contacts. Check your connection and try again.",
  retry: "Retry",
  copyFailed: "Couldn't copy the redemption link — your browser blocked clipboard access.",
} as const;

/** The query-key prefix the contacts request lives under (see `handleRetry`). */
const CONTACTS_QUERY_KEY = ["admin", "contacts"];

/**
 * The Status filter's options, built from the frontend's own vocabulary rather than from the
 * response. The five values are fixed and already known here (`CAMPAIGN_LINK_STATUS_VALUES`) and
 * the labels are the badge's own (`statusVisual`), so a failed request can no longer empty a list
 * that needs no server round-trip. The other three fields are reference data (orgs, leads,
 * campaigns) and genuinely have to come from the API.
 */
const STATUS_OPTIONS: Option[] = CAMPAIGN_LINK_STATUS_VALUES.map((status) => ({
  value: status,
  label: statusVisual(status).label,
}));

/** The chips carry plain strings; only values the API knows become `status` filters. */
function isCampaignLinkStatus(value: string): value is CampaignLinkStatus {
  return CAMPAIGN_LINK_STATUS_VALUES.some((status) => status === value);
}

/**
 * The Blotter hands `sortBy` as the clicked column's key, typed only as `string`. The API answers
 * 400 for anything outside its whitelist, so an unrecognised key is dropped here and the backend
 * falls back to its own default ("name") instead of erroring.
 */
function toSortBy(key: string): SortableContactColumnId | undefined {
  return SORTABLE_COLUMN_IDS.find((id) => id === key);
}

/** The chip values are already the ids the backend wants (they come straight from `filterOptions`). */
function toListParams(q: ContactsQuery): AdminContactListParams {
  const valuesFor = (id: string) => q.filters.find((f) => f.fieldId === id)?.values ?? [];
  return {
    search: q.search || undefined,
    organization_id: valuesFor("organization"),
    status: valuesFor("status").filter(isCampaignLinkStatus),
    account_lead_id: valuesFor("account_lead"),
    campaign_id: valuesFor("campaign"),
    page: q.page,
    limit: q.limit,
  };
}

/**
 * Builds the contacts blotter's data hook — a factory rather than a plain module-level hook
 * because of what `BlotterData` cannot carry.
 *
 * The Blotter calls `useData(q)` with its query state and reads back only
 * `{ data, total, totalPages, isLoading }` (its `BlotterData<T>`). There is no error field and no
 * channel for anything else, and D1 freezes that file — so a 500 / 403 / expired token / dead
 * network would arrive as `{ data: [], total: 0, totalPages: 1, isLoading: false }` and render as
 * the ordinary empty state, with nothing upstream to catch it (the app has no ErrorBoundary and the
 * QueryClient has no `throwOnError`). D1 says what the Blotter doesn't do is solved *around* it, so
 * both extra channels are routed around it: this hook runs inside the Blotter's render, closes over
 * two setters the panel owns, and reports `isError` AND the filter option lists upward.
 *
 * The filter lists have to come out this way because they now ride on the list response itself
 * (one endpoint, one request) — and the request belongs to the Blotter, whose query state the panel
 * deliberately cannot see.
 *
 * Both callbacks must have a stable identity — `useState` setters do. They are called from an
 * effect, never during render, and writing a value React considers unchanged makes it bail out of
 * the re-render, so neither report can loop.
 */
function makeUseContactsData(
  onError: (failed: boolean) => void,
  onFilterOptions: (options: AdminContactsFilterOptions) => void,
) {
  return function useContactsData(q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<AdminContactRow> {
    const { data, isLoading, isError } = useAdminContacts({
      ...toListParams(q),
      sortBy: toSortBy(q.sortBy),
      sortDir: q.sortDir,
      // Only the table's own request asks for them; the CSV export path below does not.
      includeFilterOptions: true,
    });

    // `onError` / `onFilterOptions` are deliberately not dependencies: they are the factory's
    // parameters, not reactive values, and React re-runs the newest closure whenever the watched
    // value changes — so they are never stale. (Biome's useExhaustiveDependencies rejects
    // outer-scope bindings here.)
    useEffect(() => {
      onError(isError);
    }, [isError]);

    const filterOptions = data?.filterOptions;
    useEffect(() => {
      if (filterOptions) onFilterOptions(filterOptions);
    }, [filterOptions]);

    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };
}

/**
 * Writes `url` to the clipboard, resolving to whether it actually landed. Both failure modes are
 * silent otherwise: the promise rejects when the browser denies clipboard access, and
 * `navigator.clipboard` is *undefined* in any non-secure context (plain http; localhost is
 * exempt), where reading `.writeText` off it throws synchronously — before a promise exists for a
 * `.catch()` to reach, so the exception escapes the click handler instead of being handled.
 */
async function writeToClipboard(url: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Rows of the Columns popover: every configurable column, labelled. Recipient is absent by design
 * — it is fixed, and `ADMIN_CONTACTS_TEXT.popoverHint` says so in the popover (R13).
 */
const COLUMN_ITEMS: readonly ColumnConfigItem<ContactColumnId>[] = DEFAULT_ORDER.map((id) => ({
  id,
  label: COLUMN_LABELS[id],
}));

export default function AdminContactsPanel() {
  // The toast's visibility and its outcome are separate state so closing it doesn't flip an error
  // toast to the success wording mid-fade; `copyFailed` simply holds until the next copy.
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [listFailed, setListFailed] = useState(false);
  const [filterOptions, setFilterOptions] = useState<AdminContactsFilterOptions | null>(null);
  const queryClient = useQueryClient();
  const prefs = useColumnPrefs<ContactColumnId>({
    paramName: COLS_PARAM,
    // DEFAULT_ORDER enumerates exactly the configurable ids, so it is both the full set and the
    // default arrangement; "name" appears in neither and can therefore never be reordered away.
    allIds: DEFAULT_ORDER,
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
  });

  // Resolved here because the cell that triggers it is a plain renderer and may not call hooks.
  // The cell asks; this owns the clipboard write, its outcome, and the toast that reports it.
  const handleCopy = useCallback((url: string) => {
    writeToClipboard(url).then((ok) => {
      setCopyFailed(!ok);
      setCopyOpen(true);
    });
  }, []);

  /**
   * Retry invalidates the whole `["admin", "contacts"]` branch — the list with whatever search /
   * filters / sort / page the Blotter currently holds, which this panel deliberately cannot see.
   * `isError` flips back to false on success, so the banner clears itself.
   */
  const handleRetry = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
  }, [queryClient]);

  const useContactsData = makeUseContactsData(setListFailed, setFilterOptions);

  const config: BlotterConfig<AdminContactRow> = {
    // No title: the admin shell's topbar already reads "Contacts" — a second heading right
    // above the search bar is the same name twice on one screen.
    searchPlaceholder: ADMIN_CONTACTS_TEXT.searchPlaceholder,
    // Drives both the footer count and the exact empty state "No contacts match your search."
    itemNoun: ADMIN_CONTACTS_TEXT.itemNoun,
    minWidth: MIN_WIDTH,
    pageSize: PAGE_SIZE,
    defaultSortKey: "name",
    rowKey: (r) => r.id,
    useData: useContactsData,
    columns: buildColumns(prefs, { onCopy: handleCopy }),
    // Decision D8's four filters, in the ticket's order. The three reference lists arrive with the
    // first page of the table itself; until they do — or if that request fails, which the banner
    // reports — each offers nothing rather than the screen withholding the table. Status is local
    // (see above).
    filterFields: [
      {
        id: "organization",
        label: "Organization",
        options: filterOptions?.organizations ?? [],
      },
      { id: "status", label: "Status", options: STATUS_OPTIONS },
      { id: "account_lead", label: "Account Lead", options: filterOptions?.accountLeads ?? [] },
      { id: "campaign", label: "Campaign", options: filterOptions?.campaigns ?? [] },
    ],
    exportFilename: ADMIN_CONTACTS_TEXT.csvFilename,
    // Same params as the table, so the CSV covers every filtered row across every page — the
    // Blotter walks the pages itself, at limit 100, in its own `handleExport`. No `includeFilterOptions`:
    // the dropdowns are already populated and every page would otherwise re-run three reference
    // queries for a list the CSV does not contain.
    exportFetch: async (q) => {
      const res = await fetchAdminContacts(toListParams(q));
      return { data: res.data, totalPages: res.totalPages };
    },
    // The prototype puts Columns before Export CSV.
    headerActionPlacement: "start",
    headerAction: (
      <ColumnConfigPopover
        items={COLUMN_ITEMS}
        order={prefs.order}
        hidden={prefs.hidden}
        fixedHint={ADMIN_CONTACTS_TEXT.popoverHint}
        onToggle={prefs.toggle}
        onMove={prefs.move}
        onReorder={prefs.reorder}
        onReset={prefs.reset}
      />
    ),
  };

  return (
    <>
      {/* Above the Blotter, never in place of it: replacing the table would throw away the
          search, filters, sort and page the user is retrying with. */}
      {listFailed && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              {FAILURE_COPY.retry}
            </Button>
          }
        >
          {FAILURE_COPY.list}
        </Alert>
      )}
      <Blotter config={config} />
      <Snackbar
        open={copyOpen}
        autoHideDuration={3000}
        onClose={() => setCopyOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={copyFailed ? "error" : "success"}
          variant="filled"
          onClose={() => setCopyOpen(false)}
        >
          {copyFailed ? FAILURE_COPY.copyFailed : ADMIN_CONTACTS_TEXT.copyToast}
        </Alert>
      </Snackbar>
    </>
  );
}
